import type { TFunction } from 'i18next';

import { diffInMs, nowISO } from '~/utils/date';

import type { RunRead } from '../types/run.api.types';
import type { RunFailureGroup, RunFailureGroups } from '../types/runs.api.types';

import { catalogLabel } from './catalog-labels';
import { STAGE_FLOW_ORDER } from './lineage-images-search';

/** Display statuses that ended short of the last step — retry targets. */
const RETRYABLE = new Set(['FAILED', 'REJECTED', 'TERMINATED']);

/** Display statuses that move on their own right now. */
const MOVING = new Set(['PENDING', 'RUNNING', 'DELIVERING', 'RESET']);

/** True when a batch stopped without finishing, so "Retry from <stage>" applies. */
export function isRetryableStatus(status: string): boolean {
  return RETRYABLE.has(status);
}

/** True while a batch advances without anyone acting (live bar, elapsed duration). */
export function isMovingStatus(status: string): boolean {
  return MOVING.has(status);
}

/**
 * How long a batch ran: start → completion, or start → now while it still moves. `null` when it
 * never started or ended without a completion time (no fake numbers).
 */
export function batchDurationMs(run: RunRead, status: string): number | null {
  if (!run.startedAt) return null;
  if (run.completedAt) return Math.max(0, diffInMs(run.startedAt, run.completedAt));
  return isMovingStatus(status) ? Math.max(0, diffInMs(run.startedAt, nowISO())) : null;
}

/** Every batch id a failure pattern names → that pattern, so a row can offer Fix & retry. */
export function patternByRunId(
  failures: RunFailureGroups | undefined,
): ReadonlyMap<string, RunFailureGroup> {
  const map = new Map<string, RunFailureGroup>();
  for (const group of failures?.groups ?? []) {
    for (const id of group.latestRunIds) {
      if (!map.has(id)) map.set(id, group);
    }
  }
  return map;
}

/** Statuses that wait on a person's call — retry or archive (the list's Retry button). */
const STOPPED = new Set(['FAILED', 'REJECTED']);

/** True when the batch stopped and the next move is someone's decision. */
export function isStoppedStatus(status: string): boolean {
  return STOPPED.has(status);
}

/** Any stage code a backend note spells out ("rejected the run at stage ENRICHMENT"). */
const STAGE_CODE_RE = new RegExp(
  `\\b(${[...STAGE_FLOW_ORDER].sort((a, b) => b.length - a.length).join('|')})\\b`,
  'g',
);

/** A free-text note with every raw stage code swapped for the step's name, so no enum leaks. */
export function withStageLabels(text: string, tCatalog: TFunction<'catalog-enrichment'>): string {
  return text.replace(STAGE_CODE_RE, (code) => catalogLabel(tCatalog, 'stage', code));
}

/**
 * True for the reason the server writes when a reviewer rejected without a note ("Operator
 * rejected the run at stage X") — not the person's words, so never quoted as their note.
 */
export function isGeneratedRejectNote(text: string | null | undefined): boolean {
  return !!text && /^operator rejected the run at stage/i.test(text.trim());
}
